<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';

// Configure axios to include credentials and CSRF token
axios.defaults.withCredentials = true;  // Important for session cookies
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Get CSRF token from meta tag
if (typeof document !== 'undefined') {
    const token = document.head.querySelector('meta[name="csrf-token"]');
    if (token) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
    } else {
        console.error('CSRF token not found');
    }
}

const props = defineProps({
    productId: {
        type: [Number, String],
        required: true
    },
    isAuthenticated: {
        type: Boolean,
        required: true
    }
});

const showListModal = ref(false);
const userLists = ref([]);
const selectedListId = ref(null);
const newListName = ref('');
const isCreatingList = ref(false);
const loading = ref(false);
const message = ref('');
const messageType = ref('success'); // 'success' or 'error'
const buttonRef = ref(null);
const dropdownRef = ref(null);
const resizeObserver = ref(null);
const listsContainingProduct = ref([]); // Track lists that contain the product

// Computed property to check if product is in any list
const isProductInAnyList = computed(() => {
    return listsContainingProduct.value.length > 0;
});

// Position the dropdown relative to the button with viewport awareness
const positionDropdown = () => {
    if (typeof window === 'undefined' || !buttonRef.value || !dropdownRef.value) return;
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Get button dimensions and position
    const buttonRect = buttonRef.value.getBoundingClientRect();
    
    // Get dropdown dimensions
    const dropdownWidth = dropdownRef.value.offsetWidth;
    const dropdownHeight = dropdownRef.value.offsetHeight;
    
    // Calculate available space in different directions
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const spaceRight = viewportWidth - buttonRect.left;
    const spaceLeft = buttonRect.right;
    
    // Default position (starts with preferred position - below button)
    let top, left;
    
    // Determine vertical position (top or bottom)
    if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
        // Position below button
        top = buttonRect.bottom + window.scrollY + 5;
    } else {
        // Position above button
        top = buttonRect.top + window.scrollY - dropdownHeight - 5;
    }
    
    // Determine horizontal position
    if (buttonRect.left + dropdownWidth <= viewportWidth) {
        // Align with left edge of button if it fits
        left = buttonRect.left + window.scrollX;
    } else {
        // Align with right edge of button and extend left
        left = buttonRect.right + window.scrollX - dropdownWidth;
    }
    
    // Ensure dropdown stays within viewport horizontally
    left = Math.max(5 + window.scrollX, Math.min(left, viewportWidth - dropdownWidth - 5 + window.scrollX));
    
    // Apply position
    dropdownRef.value.style.top = `${top}px`;
    dropdownRef.value.style.left = `${left}px`;
};

// Watch for modal visibility
watch(showListModal, async (newValue) => {
    if (newValue && props.isAuthenticated) {
        await fetchUserLists();
        
        // Position after a tiny delay to ensure DOM is updated
        setTimeout(() => {
            positionDropdown();
            
            // Set up resize observer if not already
            setupResizeObserver();
        }, 10);
    } else {
        // Clean up resize observer when modal closes
        clearResizeObserver();
    }
});

// Handle window resize
const handleResize = () => {
    if (typeof window !== 'undefined' && showListModal.value && dropdownRef.value) {
        positionDropdown();
    }
};

// Set up resize observer to monitor size changes
const setupResizeObserver = () => {
    if (typeof window === 'undefined' || resizeObserver.value) return;
    
    resizeObserver.value = new ResizeObserver(() => {
        if (showListModal.value && dropdownRef.value) {
            positionDropdown();
        }
    });
    
    if (dropdownRef.value) {
        resizeObserver.value.observe(dropdownRef.value);
    }
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
};

// Clean up resize observer
const clearResizeObserver = () => {
    if (resizeObserver.value) {
        resizeObserver.value.disconnect();
        resizeObserver.value = null;
    }
    
    if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize);
    }
};

// Close dropdown if clicked outside
const handleClickOutside = (event) => {
    if (typeof document !== 'undefined' && showListModal.value && dropdownRef.value && 
        !dropdownRef.value.contains(event.target) && 
        buttonRef.value && !buttonRef.value.contains(event.target)) {
        showListModal.value = false;
    }
};

// Add event listener for outside clicks
watch(showListModal, (value) => {
    if (typeof document !== 'undefined') {
        if (value) {
            document.addEventListener('click', handleClickOutside);
        } else {
            document.removeEventListener('click', handleClickOutside);
            clearResizeObserver();
        }
    }
});

// Clean up event listeners
onMounted(() => {
    if (typeof document !== 'undefined') {
        document.addEventListener('click', handleClickOutside);
    }
    
    // Check if product is in any list on component mount
    if (props.isAuthenticated) {
        fetchUserLists();
    }
});

onUnmounted(() => {
    if (typeof document !== 'undefined') {
        document.removeEventListener('click', handleClickOutside);
    }
    clearResizeObserver();
});

const fetchUserLists = async () => {
    if (!props.isAuthenticated) return;
    
    try {
        loading.value = true;
        // Use web route instead of API route
        const response = await axios.get('/web-api/user/product-lists', {
            params: {
                product_id: props.productId // Send product ID to check which lists contain it
            },
            withCredentials: true,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
            }
        });
        userLists.value = response.data;
        
        // Extract lists that contain the product
        listsContainingProduct.value = userLists.value
            .filter(list => list.contains_product === true)
            .map(list => list.id);
            
        loading.value = false;
    } catch (error) {
        console.error('Error fetching user lists:', error);
        loading.value = false;
        
        // Debug response
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
    }
};

const createNewList = async () => {
    if (!newListName.value.trim()) {
        message.value = 'Listens navn kan ikke være tomt';
        messageType.value = 'error';
        return;
    }
    
    try {
        loading.value = true;
        // Use web route instead of API route
        const response = await axios.post('/web-api/user/product-lists', 
            { name: newListName.value.trim() },
            {
                withCredentials: true,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            }
        );
        
        // Add new list to the list array
        userLists.value.push(response.data);
        
        // Select the newly created list
        selectedListId.value = response.data.id;
        
        // Reset form
        newListName.value = '';
        isCreatingList.value = false;
        
        message.value = 'Listen blev oprettet';
        messageType.value = 'success';
        loading.value = false;
    } catch (error) {
        console.error('Error creating list:', error);
        message.value = 'Der opstod en fejl ved oprettelse af listen: ' + 
                       (error.response?.data?.message || error.message);
        messageType.value = 'error';
        loading.value = false;
        
        // Debug authentication status
        try {
            const authCheck = await axios.get('/api/auth/check');
        } catch (authError) {
            console.error('Auth check failed:', authError);
        }
    }
};

const addToList = async () => {
    if (!selectedListId.value) {
        message.value = 'Vælg venligst en liste';
        messageType.value = 'error';
        return;
    }
    
    try {
        loading.value = true;
        // Use web route instead of API route
        await axios.post('/web-api/user/product-lists/add-product', 
            {
                list_id: selectedListId.value,
                product_id: props.productId
            },
            {
                withCredentials: true,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            }
        );
        
        // Update the lists containing the product
        if (!listsContainingProduct.value.includes(selectedListId.value)) {
            listsContainingProduct.value.push(selectedListId.value);
        }
        
        message.value = 'Produkt tilføjet til listen';
        messageType.value = 'success';
        loading.value = false;
        
        // Close modal after a short delay to show success message
        setTimeout(() => {
            showListModal.value = false;
            message.value = '';
        }, 1500);
    } catch (error) {
        if (error.response && error.response.status === 409) {
            message.value = 'Produktet findes allerede i denne liste';
        } else {
            message.value = 'Der opstod en fejl ved tilføjelse af produktet til listen: ' +
                           (error.response?.data?.message || error.message);
        }
        messageType.value = 'error';
        loading.value = false;
    }
};

const openModal = (event) => {
    if (!props.isAuthenticated) {
        router.visit('/login?redirect=' + encodeURIComponent(window.location.pathname));
        return;
    }
    
    // Prevent event propagation
    event.stopPropagation();
    
    showListModal.value = !showListModal.value;
};
</script>

<template>
    <div class="list-dropdown-container">
        <button 
            ref="buttonRef"
            type="button" 
            class="btn" 
            :class="isProductInAnyList ? 'btn-success' : 'btn-outline-secondary'"
            @click="openModal"
            title="Tilføj til liste"
        >
            <font-awesome-icon 
                :icon="isProductInAnyList ? 'fa-solid fa-check-circle' : 'fa-solid fa-list'" 
                class="me-2" 
            />
            <span class="d-none d-md-inline">
                {{ isProductInAnyList ? 'På liste' : 'Tilføj til liste' }}
            </span>
            <span v-if="isProductInAnyList" class="badge bg-white text-success ms-1">
                {{ listsContainingProduct.length }}
            </span>
        </button>

        <!-- Dropdown menu instead of modal -->
        <div 
            v-if="showListModal" 
            ref="dropdownRef"
            class="list-context-menu"
            @click.stop
        >
            <div class="dropdown-header">
                <h5 class="menu-title">Tilføj til liste</h5>
                <button type="button" class="btn-close btn-sm" @click.stop="showListModal = false" aria-label="Close"></button>
            </div>

            <div class="dropdown-body">
                <div v-if="loading" class="text-center my-3">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">Indlæser...</span>
                    </div>
                </div>

                <div v-else>
                    <!-- Messages -->
                    <div v-if="message" :class="`alert alert-${messageType === 'success' ? 'success' : 'danger'} py-2 mb-2 small`" role="alert">
                        {{ message }}
                    </div>

                    <!-- List selector with indicators for lists containing the product -->
                    <div v-if="!isCreatingList" class="mb-2">
                        <p class="menu-subtitle mb-1">Vælg en liste:</p>
                        <div class="list-group list-group-sm mb-2">
                            <button 
                                v-for="list in userLists" 
                                :key="list.id"
                                type="button"
                                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2"
                                :class="{ 
                                    'active': selectedListId === list.id,
                                    'list-contains-product': listsContainingProduct.includes(list.id) 
                                }"
                                @click.stop="selectedListId = list.id"
                            >
                                <span class="d-flex align-items-center">
                                    <font-awesome-icon 
                                        v-if="listsContainingProduct.includes(list.id)"
                                        icon="fa-solid fa-check" 
                                        class="me-2 list-product-indicator" 
                                    />
                                    {{ list.list_name }}
                                </span>
                                <span class="badge bg-primary rounded-pill" v-if="list.product_count">
                                    {{ list.product_count }}
                                </span>
                            </button>
                        </div>

                        <div v-if="userLists.length === 0" class="text-center text-muted my-2 small">
                            Du har ingen lister endnu.
                        </div>

                        <button 
                            type="button" 
                            class="btn btn-outline-primary btn-sm w-100"
                            @click.stop="isCreatingList = true"
                        >
                            <font-awesome-icon icon="fa-solid fa-plus" class="me-1" />
                            Opret ny liste
                        </button>
                    </div>

                    <!-- Create new list form -->
                    <div v-else class="mb-2">
                        <p class="menu-subtitle mb-1">Opret ny liste:</p>
                        <div class="input-group input-group-sm mb-2">
                            <input 
                                type="text" 
                                class="form-control" 
                                placeholder="Listens navn" 
                                v-model="newListName"
                                @keyup.enter.stop="createNewList"
                            >
                            <button 
                                class="btn btn-primary btn-sm" 
                                type="button"
                                @click.stop="createNewList"
                            >
                                Opret
                            </button>
                        </div>
                        <button 
                            type="button" 
                            class="btn btn-link btn-sm text-decoration-none p-0"
                            @click.stop="isCreatingList = false"
                        >
                            &larr; Tilbage til lister
                        </button>
                    </div>

                    <div v-if="!isCreatingList" class="d-flex justify-content-between mt-3">
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click.stop="showListModal = false">Luk</button>
                        <button 
                            type="button" 
                            class="btn btn-sm btn-pricecompare-primary" 
                            @click.stop="addToList"
                            :disabled="!selectedListId || loading"
                        >
                            <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Tilføj
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.list-dropdown-container {
    position: relative;
    display: inline-block;
}

.list-context-menu {
    position: fixed; /* Using fixed instead of absolute to prevent issues with parent positioning */
    z-index: 1050;
    width: 280px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.15);
    overflow: hidden;
}

.dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.menu-title {
    font-size: 1rem;
    font-weight: 500;
    margin: 0;
}

.dropdown-body {
    padding: 0.75rem;
    max-height: 350px;
    overflow-y: auto;
}

.menu-subtitle {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6c757d;
}

.list-group-sm .list-group-item {
    padding: 0.4rem 0.75rem;
    font-size: 0.875rem;
}

.list-group-item.active {
    background-color: #1E3A8A;
    border-color: #1E3A8A;
}

.list-contains-product {
    border-left: 3px solid #198754 !important;
    background-color: rgba(25, 135, 84, 0.05);
}

.list-product-indicator {
    color: #198754;
    font-size: 0.8rem;
}

/* Make active state override the list-contains-product style */
.list-group-item.active.list-contains-product {
    background-color: #1E3A8A;
    border-color: #1E3A8A;
}

.list-group-item.active .list-product-indicator {
    color: white;
}

.btn-success {
    background-color: #198754;
    border-color: #198754;
    color: white;
}

.btn-success .badge {
    color: #198754;
}

/* Mobile adjustments */
@media (max-width: 767.98px) {
    .list-context-menu {
        width: 250px;
    }
}
</style>
